import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import "../styling/TilesPage.css";
import Tile from "../components/Tile";
import CategoryHeader from "../components/CategoryHeader";
import FiltersBar from "../components/FiltersBar";
import PublicationsList from "../components/PublicationsList";
import Tabs from "../components/Tabs";
import { clearField } from "../lib/cf";
import { TILES, CATEGORY_HEADERS } from "../data/tiles";

// relative URL for Docker/nginx setup
const API_BASE = "/api";
const EMPTY_DATA = [];
const CACHE_TTL = 5 * 60 * 1000;
const CACHE_LIMIT = 20;


function DashboardTab({ filters, setFilters, dashboardData, dashboardLoading, dashboardError }) {
	const hasLoadedRef = useRef(false);
	useEffect(() => {
		if (!dashboardLoading && !dashboardError) hasLoadedRef.current = true;
	}, [dashboardLoading, dashboardError]);

	//keep the current charts on screen (dimmed) instead of tearing them down
	const isRefreshing = dashboardLoading && hasLoadedRef.current;

	if (dashboardError) {
		return (
			<div className="grid dashboard-error">
				<div className="dashboard-error-title">Failed to load dashboard {dashboardError}</div>
			</div>
		);
	}

	return (
		<div className={`grid${isRefreshing ? " is-refreshing" : ""}`}>
			{CATEGORY_HEADERS.map((h) => (
				<CategoryHeader
					key={h.key}
					label={h.label}
					count={dashboardData.stats?.[h.statKey] ?? 0}
					countLabel={h.countLabel}
					color={h.color}
					bgColor={h.bgColor}
					loading={dashboardLoading}
					style={{
						gridColumn: `${h.col} / span ${h.colSpan}`,
						gridRow: h.row,
					}}
				/>
			))}

			{TILES.map(({ key, title, iconHover, description, Component, tileProps, facetProps }, index) => {
				const bucket = dashboardData?.[key];
				const tileData = bucket?.data || EMPTY_DATA;

				const isVisible = !dashboardLoading || hasLoadedRef.current;

				return (
					<Tile
						key={key}
						title={title}
						iconHover={iconHover}
						description={description}
						onClear={() => clearField(setFilters, key)}
						hasFilter={!!(filters.get(key)?.size)}
						{...tileProps}
					>
						{isVisible ? (
							<Component
								filters={filters}
								setFilters={setFilters}
								data={tileData}
								field={facetProps?.field || key}
								loading={dashboardLoading && !isRefreshing}
								{...facetProps}
							/>
						) : (
							<div className="facet-loading">
								<div className="spinner"></div>
								<div className="loading-text">Loading chart...</div>
							</div>
						)}
					</Tile>
				);
			})}
		</div>
	);
}

const MemoDashboardTab = memo(DashboardTab);
const MemoPublicationsList = memo(PublicationsList);

export default function TilesPage() {
	const [manuscripts, setManuscripts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [filters, setFilters] = useState(new Map());
	const [tab, setTab] = useState("dashboard");
	const [publicationsVisited, setPublicationsVisited] = useState(false);
	const [publicationsCache, setPublicationsCache] = useState(new Map());
	const dashboardCache = useRef(new Map());
	const changeTab = useCallback((nextTab) => {
		if (nextTab === "publications") setPublicationsVisited(true);
		setTab(nextTab);
	}, []);

	const [dashboardLoading, setDashboardLoading] = useState(true);
	const [dashboardData, setDashboardData] = useState({
		stats: {
			publications: 0,
			patients: 0,
			treatments: 0,
		},
	});
	const [dashboardError, setDashboardError] = useState(null);

	useEffect(() => {
		fetchManuscripts();
	}, []);


	const filterBody = useMemo(() => {
		const body = {};
		Array.from(filters.keys()).sort().forEach((key) => {
			const values = filters.get(key);
			if (values?.size) body[key] = Array.from(values).sort();
		});
		return JSON.stringify(body);
	}, [filters]);

	useEffect(() => {
		const controller = new AbortController();

		const fetchDashboard = async () => {
			try {
				setDashboardLoading(true);
				setDashboardError(null);

				const cached = dashboardCache.current.get(filterBody);
				if (cached && Date.now() - cached.time < CACHE_TTL) {
					setDashboardData(cached.data);
					return;
				}

				const res = await fetch(`${API_BASE}/analytics/all`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: filterBody,
					signal: controller.signal,
				});

				if (!res.ok) {
					throw new Error(`API failed ${res.status}`);
				}

				const json = await res.json();
				if (controller.signal.aborted) return;
				dashboardCache.current.delete(filterBody);
				dashboardCache.current.set(filterBody, { data: json || {}, time: Date.now() });
				if (dashboardCache.current.size > CACHE_LIMIT) {
					dashboardCache.current.delete(dashboardCache.current.keys().next().value);
				}
				setDashboardData(json || {});
			} catch (err) {
				if (err.name === "AbortError") return;

				console.error("[DASHBOARD_ALL_ERROR]", err);

				setDashboardError(err.message);

				setDashboardData({
					stats: {
						publications: 0,
						patients: 0,
						treatments: 0,
					},
				});
			} finally {
				if (!controller.signal.aborted) {
					setDashboardLoading(false);
				}
			}
		};

		fetchDashboard();

		return () => controller.abort();
	}, [filterBody]);

	async function fetchManuscripts() {
		try {
			setLoading(true);
			setError(null);

			const manuscriptsRes = await fetch(`${API_BASE}/manuscripts/summary`);

			if (!manuscriptsRes.ok) {
				throw new Error("Failed to fetch data");
			}

			const manuscriptsData = await manuscriptsRes.json();
			const nextManuscripts =
				manuscriptsData.data?.manuscripts || manuscriptsData.manuscripts || [];

			setManuscripts(nextManuscripts);
		} catch (err) {
			console.error("Failed to load data:", err);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}

	//id -> title lookup so the filter chips can show titles instead of ids
	const manuscriptTitles = useMemo(() => {
		const map = new Map();
		manuscripts.forEach((m) => map.set(String(m.id), m.title));
		return map;
	}, [manuscripts]);

	const updateFilters = useCallback((updater) => {
		setFilters(updater);
	}, []);

	if (error) {
		return (
			<main className="app">
				<div style={{ padding: "40px", textAlign: "center", color: "#DC2626" }}>
					<div style={{ fontSize: "16px", marginBottom: "8px", fontWeight: 600 }}>
						Error loading publications
					</div>
					<div style={{ fontSize: "14px", color: "#6B7280", marginBottom: "16px" }}>
						{error}
					</div>
				</div>
			</main>
		);
	}

	return (
		<div className={`tiles-page${tab === "publications" ? " tiles-page--fill" : ""}`}>
			<div className="sticky-header">
				<Tabs
					value={tab}
					onChange={changeTab}
					items={[
						{ value: "dashboard", label: "Summary" },
						{ value: "publications", label: "Publications" },
					]}
				/>

				<div className="publications-number">
					{dashboardLoading ? (
						<span className="stats-loading">
							<div className="spinner" style={{ width: "14px", height: "14px" }}></div>
						</span>
					) : dashboardError ? (
						<span style={{ color: "#DC2626", fontSize: "13px" }}>
							⚠ Failed to load data
						</span>
					) : (
						<span>
							{(dashboardData.stats?.patients ?? 0).toLocaleString()} patient
							{dashboardData.stats?.patients === 1 ? "" : "s"} ·{" "}
							{(dashboardData.stats?.treatments ?? 0).toLocaleString()} treatment
							{dashboardData.stats?.treatments === 1 ? "" : "s"} ·{" "}
							{(dashboardData.stats?.publications ?? 0).toLocaleString()} publication
							{dashboardData.stats?.publications === 1 ? "" : "s"}
						</span>
					)}

				</div>

				<FiltersBar
					filters={filters}
					setFilters={updateFilters}
					manuscriptTitles={manuscriptTitles}
				/>
			</div>

			<main className="app">
				<div style={{ display: tab === "dashboard" ? "block" : "none" }}>
					<MemoDashboardTab
						filters={filters}
						setFilters={updateFilters}
						dashboardData={dashboardData}
						dashboardLoading={dashboardLoading}
						dashboardError={dashboardError}
					/>
				</div>
				{publicationsVisited && (
					<div className="pubs-tab" style={tab === "publications" ? undefined : { display: "none" }}>
						<MemoPublicationsList
							filters={filters}
							setFilters={updateFilters}
							selectable
							manuscripts={manuscripts}
							publicationsCache={publicationsCache}
							setPublicationsCache={setPublicationsCache}
							isLoading={loading}
						/>
					</div>
				)}
			</main>
		</div>
	);
}